/**
 * useSfxGeneration - Delegates job lifecycle to GenerationContext so that
 * progress + result survive tab switches and page reloads.
 *
 * Extracted from SfxGenerationPanel; the panel's public-facing state
 * (isGenerating, progress, audioUrl, error) maps 1-to-1 to what it had
 * before so SfxGenerationPanel needs only minor changes.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useGeneration } from '../contexts/GenerationContext.context';
import { useGenerationJob } from './useGenerationJob';
import { SoundEffectsAPI } from '../services/soundEffectsAPI';
import type { SfxGenerationRequest, SfxVideoToAudioRequest } from '../services/soundEffectsAPI';
import type { SoundEffectsJobResult } from '../services/polling/soundEffectsPollAdapter';

export interface UseSfxGenerationReturn {
  isGenerating: boolean;
  /** Current progress (0-100) */
  progress: number;
  audioUrl: string | null;
  /**
   * Re-read the finished job for a freshly signed audio link. `audioUrl`
   * expires an hour after the backend signed it; the player and the download
   * ask for this before using it. Rejects when the job can no longer be read.
   */
  renewAudioUrl: () => Promise<string>;
  error: string | null;
  generateSfx: (request: SfxGenerationRequest) => Promise<void>;
  generateSfxFromVideo: (request: SfxVideoToAudioRequest) => Promise<void>;
  reset: () => void;
}

export function useSfxGeneration(): UseSfxGenerationReturn {
  const { startJob } = useGeneration();

  const [startError, setStartError] = useState<string | null>(null);
  const lastNotifiedJobRef = useRef<string | null>(null);

  // Backend returns progress 0.0-1.0; adapter converts to 0-100 before storing,
  // so the raw backend progress is already 0-100 here (no expectedSeconds).
  const { activeJob, isGenerating, progress, result, jobError, clearJobs, refreshResult } =
    useGenerationJob<SoundEffectsJobResult>('soundEffects');

  const audioUrl = result?.audioUrl ?? null;
  const renewAudioUrl = useCallback(
    async (): Promise<string> => (await refreshResult()).audioUrl,
    [refreshResult]
  );

  const error = startError ?? jobError;

  // Clear stale error notifications when a new job starts
  useEffect(() => {
    if (activeJob && lastNotifiedJobRef.current !== activeJob.id) {
      lastNotifiedJobRef.current = null;
    }
  }, [activeJob]);

  const generateSfx = useCallback(
    async (request: SfxGenerationRequest): Promise<void> => {
      setStartError(null);
      try {
        const submitResponse = await SoundEffectsAPI.submitSfxGeneration(request);
        startJob<SfxGenerationRequest>({
          id: submitResponse.job_id,
          tabMode: 'soundEffects',
          serverType: 'soundeffects',
          metadata: request,
          title: 'Sound effect generation',
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to start sound effect generation';
        setStartError(message);
      }
    },
    [startJob]
  );

  const generateSfxFromVideo = useCallback(
    async (request: SfxVideoToAudioRequest): Promise<void> => {
      setStartError(null);
      try {
        const submitResponse = await SoundEffectsAPI.submitSfxFromVideo(
          request.provider,
          request.video_file,
          {
            prompt: request.prompt,
            duration: request.duration,
            seed: request.seed,
          }
        );
        startJob<SfxVideoToAudioRequest>({
          id: submitResponse.job_id,
          tabMode: 'soundEffects',
          serverType: 'soundeffects',
          metadata: request,
          title: 'Sound effect generation (video)',
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to start video-to-audio generation';
        setStartError(message);
      }
    },
    [startJob]
  );

  const reset = useCallback((): void => {
    setStartError(null);
    lastNotifiedJobRef.current = null;
    clearJobs();
  }, [clearJobs]);

  return {
    isGenerating,
    progress,
    audioUrl,
    renewAudioUrl,
    error,
    generateSfx,
    generateSfxFromVideo,
    reset,
  };
}

export default useSfxGeneration;
